// planHoldFilter — pure logic for deciding which holds and procedure
// turns in a flight plan should render on the en-route map.
//
// Lives in `lib/` so it can be unit-tested without pulling MapLibre,
// stores, or other DOM-dependent modules. PlanProceduresLayer in the
// `map/` folder calls these to drive the React-side rendering.
//
// Rules (boss spec, 2026-05-15):
//   1. Manually-added holds (e.g. via the Garmin-style hold-at-fix
//      dialog) ALWAYS render.
//   2. Missed-approach holds are SUPPRESSED by default.
//      EXCEPTION: when the hold's fix is also a charted IAF on the
//      same procedure, the published missed-approach hold serves
//      double duty as a HILPT (KMFR R14-Y SAMIE is a textbook case).
//      In that case we fall through to the course-reversal check.
//   3. Otherwise: course-reversal-required check using the arrival
//      heading derived from the route's enroute geometry. If the
//      arrival heading lands in the AIM 5-3-8 direct-entry sector
//      (±70° of inbound) the hold is suppressed. Otherwise rendered.

import { bearingDeg } from './geo';
import { classifyHoldEntry } from './holdingPattern';
import type { ExtendedPlanPoint } from './procedureExpansion';

export interface HoldRow {
  fixIdent: string;
  fixLat: number;
  fixLon: number;
  inboundCourseTrueDeg: number;
  turnDirection: 'L' | 'R';
  holdLeg: { kind: 'time'; minutes: number } | { kind: 'distance'; nm: number } | null;
  isMissedApproach: boolean;
  manuallyAdded: boolean;
  routeIndex: number;
}

export interface PtRow {
  fixIdent: string;
  fixLat: number;
  fixLon: number;
  inboundCourseTrueDeg: number;
  turnDirection: 'L' | 'R';
  manuallyAdded: boolean;
  routeIndex: number;
}

/**
 * Walk the route looking for procedure-tagged hold legs (HM/HF/HA or
 * isHoldingPattern=true). Drops legs with missing or sentinel-zero
 * coordinates and legs without an inbound course.
 */
/**
 * Recover an inbound course for a hold whose CIFP record is missing
 * the `holdInboundCourse` field. Strategy:
 *   1. Use the great-circle bearing from the previous non-same-coord
 *      renderable route point TO the hold fix. The aircraft will be on
 *      that course as it crosses the fix on the inbound segment, which
 *      is exactly the AIM definition of the hold's inbound course.
 *   2. If no upstream context exists (the hold is the first plan point),
 *      return null and let the caller drop the hold entirely.
 */
function recoverInboundCourse(
  route: ExtendedPlanPoint[],
  idx: number,
  fixLat: number,
  fixLon: number,
): number | null {
  for (let i = idx - 1; i >= 0; i--) {
    const prev = route[i];
    if (!prev) continue;
    if (prev.procedureTag?.placeholder) continue;
    if (!Number.isFinite(prev.lat) || !Number.isFinite(prev.lon)) continue;
    if (prev.lat === 0 && prev.lon === 0) continue;
    const dx = Math.abs(prev.lat - fixLat);
    const dy = Math.abs(prev.lon - fixLon);
    if (dx < 0.0001 && dy < 0.0001) continue;
    return bearingDeg(prev.lat, prev.lon, fixLat, fixLon);
  }
  return null;
}

export function extractHoldsFromRoute(route: ExtendedPlanPoint[]): HoldRow[] {
  const out: HoldRow[] = [];
  route.forEach((pt, idx) => {
    const tag = pt.procedureTag;
    if (!tag) return;
    const isHold =
      tag.isHoldingPattern === true ||
      tag.legType === 'HM' || tag.legType === 'HF' || tag.legType === 'HA';
    if (!isHold) return;
    if (pt.lat == null || pt.lon == null) return;
    if (pt.lat === 0 && pt.lon === 0) return;
    // CIFP usually carries holdInboundCourse on every HM/HF/HA leg, but
    // a fraction of older records emit blank. When that happens, fall
    // back to deriving the inbound course from the leg INTO the fix in
    // route order (which is what the aircraft will be flying when it
    // crosses the fix anyway). If no upstream context exists, drop the
    // hold — it would render as a bogus zero-degree racetrack otherwise.
    let inboundCourse = tag.holdInboundCourse;
    if (inboundCourse == null) {
      inboundCourse = recoverInboundCourse(route, idx, pt.lat, pt.lon);
      if (inboundCourse == null) return;
    }
    out.push({
      fixIdent: pt.ident,
      fixLat: pt.lat,
      fixLon: pt.lon,
      inboundCourseTrueDeg: inboundCourse,
      // Turn direction defaults to R (standard) when CIFP omits it.
      turnDirection: tag.holdTurn === 'L' ? 'L' : 'R',
      // Leg length defaults to 1-min time (AIM standard) when CIFP omits.
      holdLeg: tag.holdLeg ?? null,
      isMissedApproach: tag.isMissedApproach === true,
      manuallyAdded: tag.manuallyAdded === true,
      routeIndex: idx,
    });
  });
  return out;
}

/**
 * Walk the route looking for procedure-turn legs (legType='PI').
 */
export function extractPtsFromRoute(route: ExtendedPlanPoint[]): PtRow[] {
  const out: PtRow[] = [];
  route.forEach((pt, idx) => {
    const tag = pt.procedureTag;
    if (!tag) return;
    if (tag.legType !== 'PI') return;
    if (pt.lat == null || pt.lon == null) return;
    if (pt.lat === 0 && pt.lon === 0) return;
    // Same recovery dance as holds: PI legs sometimes omit
    // holdInboundCourse but we can derive it from upstream geometry.
    let inboundCourse = tag.holdInboundCourse;
    if (inboundCourse == null) {
      inboundCourse = recoverInboundCourse(route, idx, pt.lat, pt.lon);
      if (inboundCourse == null) return;
    }
    out.push({
      fixIdent: pt.ident,
      fixLat: pt.lat,
      fixLon: pt.lon,
      inboundCourseTrueDeg: inboundCourse,
      turnDirection: tag.holdTurn === 'L' ? 'L' : 'R',
      manuallyAdded: tag.manuallyAdded === true,
      routeIndex: idx,
    });
  });
  return out;
}

/**
 * Locate the IAF route point that shares the hold's fix on the same
 * procedure, if one exists.
 *
 * A growing number of FAA approaches publish the missed-approach hold
 * at the SAME fix as a charted IAF (e.g. KMFR R14-Y SAMIE: SAMIE is
 * the IAF for the ASAMI transition AND the missed-approach holding
 * fix). When that fix is also the only place a pilot could enter the
 * approach from the wrong side and need course reversal, the published
 * missed-approach hold does double duty as the de facto HILPT.
 *
 * Returns the route index of the dual-purpose IAF, or null.
 */
export function findDualPurposeIafIndex(
  hold: HoldRow,
  route: ExtendedPlanPoint[],
): number | null {
  const holdPt = route[hold.routeIndex];
  const procIdent = holdPt?.procedureTag?.procIdent;
  if (!procIdent) return null;
  for (let i = 0; i < route.length; i++) {
    const pt = route[i];
    if (i === hold.routeIndex) continue;
    const tag = pt.procedureTag;
    if (!tag) continue;
    if (tag.procIdent !== procIdent) continue;
    if (!tag.isIaf) continue;
    if (pt.ident.toUpperCase() === hold.fixIdent.toUpperCase()) return i;
  }
  return null;
}

/**
 * Compute the arrival heading at a given route index, ignoring upstream
 * points that share the same coordinates (handles the 'fly to fix, then
 * hold' encoding where DF and HM are stored at the same lat/lon).
 *
 * Used by the dual-purpose hold case to measure arrival heading at the
 * IAF (where the aircraft first crosses the fix) rather than at the
 * missed-approach-segment HM (where the aircraft is climbing out from
 * the runway and approaching the same fix from a different direction).
 */
export function arrivalHeadingAtRouteIndex(
  route: ExtendedPlanPoint[],
  index: number,
  targetLat: number,
  targetLon: number,
): number | null {
  for (let i = index - 1; i >= 0; i--) {
    const prev = route[i];
    if (!prev) continue;
    if (prev.procedureTag?.placeholder) continue;
    if (!Number.isFinite(prev.lat) || !Number.isFinite(prev.lon)) continue;
    if (prev.lat === 0 && prev.lon === 0) continue;
    const dx = Math.abs(prev.lat - targetLat);
    const dy = Math.abs(prev.lon - targetLon);
    if (dx < 0.0001 && dy < 0.0001) continue;
    return bearingDeg(prev.lat, prev.lon, targetLat, targetLon);
  }
  return null;
}

function courseReversalRequired(
  fixLat: number,
  fixLon: number,
  inboundCourseTrueDeg: number,
  turnDirection: 'L' | 'R',
  arrivalHeadingTrueDeg: number,
): boolean {
  const entryType = classifyHoldEntry(
    {
      fixLat,
      fixLon,
      inboundCourseTrueDeg,
      turnDirection,
      legLength: null,
    },
    { arrivalHeadingTrueDeg },
  );
  return entryType !== 'direct';
}

export function filterHolds(
  holds: HoldRow[],
  route: ExtendedPlanPoint[],
): { rendered: HoldRow[]; suppressed: HoldRow[] } {
  const rendered: HoldRow[] = [];
  const suppressed: HoldRow[] = [];
  for (const h of holds) {
    if (h.manuallyAdded) {
      rendered.push(h);
      continue;
    }
    let arrivalEvalIndex = h.routeIndex;
    if (h.isMissedApproach) {
      const iafIdx = findDualPurposeIafIndex(h, route);
      if (iafIdx == null) {
        suppressed.push(h);
        continue;
      }
      arrivalEvalIndex = iafIdx;
    }
    const arrivalHdg = arrivalHeadingAtRouteIndex(
      route, arrivalEvalIndex, h.fixLat, h.fixLon,
    );
    if (arrivalHdg == null) {
      // No upstream context — can't classify entry sector. Render to be
      // safe (the user added a procedure with a HILPT and we can't
      // prove they're already aligned).
      rendered.push(h);
      continue;
    }
    const needed = courseReversalRequired(
      h.fixLat, h.fixLon,
      h.inboundCourseTrueDeg,
      h.turnDirection,
      arrivalHdg,
    );
    if (needed) {
      rendered.push(h);
    } else {
      suppressed.push(h);
    }
  }
  return { rendered, suppressed };
}

function arrivalHeadingForPt(
  route: ExtendedPlanPoint[],
  pt: PtRow,
): number | null {
  return arrivalHeadingAtRouteIndex(route, pt.routeIndex, pt.fixLat, pt.fixLon);
}

export function filterPts(
  pts: PtRow[],
  route: ExtendedPlanPoint[],
): { rendered: PtRow[]; suppressed: PtRow[] } {
  const rendered: PtRow[] = [];
  const suppressed: PtRow[] = [];
  for (const p of pts) {
    if (p.manuallyAdded) {
      rendered.push(p);
      continue;
    }
    const arrivalHdg = arrivalHeadingForPt(route, p);
    if (arrivalHdg == null) {
      rendered.push(p);
      continue;
    }
    const needed = courseReversalRequired(
      p.fixLat, p.fixLon,
      p.inboundCourseTrueDeg,
      p.turnDirection,
      arrivalHdg,
    );
    if (needed) {
      rendered.push(p);
    } else {
      suppressed.push(p);
    }
  }
  return { rendered, suppressed };
}
